import React, { useState, useEffect, useCallback, useRef } from "react";
import { GoogleMap, Marker, Autocomplete } from "@react-google-maps/api";
import { MapPin } from "lucide-react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

const DEFAULT_CENTER = { lat: 43.6532, lng: -79.3832 };

export const MapLocationSelector = ({
  label = "Select Location",
  latitude,   // SOURCE OF TRUTH
  longitude,  // SOURCE OF TRUTH
  address,    // Optional initial text
  onChange,
  isLoaded,
  disabled = false,
  error,
}) => {
  const [map, setMap] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  
  // INTERNAL STATE: This ensures the input shows text even if parent has none
  const [inputValue, setInputValue] = useState(address || "");

  // Initialize Map & Geocoder
  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    setGeocoder(new window.google.maps.Geocoder());
  }, []);

  // 1. Sync Map Center with Lat/Long
  useEffect(() => {
    if (latitude && longitude) {
      setCenter({ lat: parseFloat(latitude), lng: parseFloat(longitude) });
    } else if (!latitude && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => console.log("Geolocation denied")
      );
    }
  }, [latitude, longitude]);

  // 2. CRITICAL FIX: IF WE HAVE COORDS BUT NO TEXT -> FETCH IT
  useEffect(() => {
    if (isLoaded && geocoder && latitude && longitude) {
      // Check if our internal input is empty. If so, we MUST fetch.
      if (!inputValue || inputValue === "") {
        console.log("Coords exist but text missing. Fetching from Google...");
        
        geocoder.geocode(
          { location: { lat: parseFloat(latitude), lng: parseFloat(longitude) } },
          (results, status) => {
            if (status === "OK" && results[0]) {
              const foundAddress = results[0].formatted_address;
              
              // 1. Fill the Input Box
              setInputValue(foundAddress);
              
              // 2. Update Parent (so it has the text for saving later if needed)
              onChange({
                address: foundAddress,
                lat: parseFloat(latitude),
                lng: parseFloat(longitude),
              });
            }
          }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, geocoder, latitude, longitude]); // Dependencies carefully chosen to run once when data loads

  // Handle Dragging Map
  const onMapDragEnd = useCallback(() => {
    if (map && geocoder) {
      const newCenter = map.getCenter();
      const lat = newCenter.lat();
      const lng = newCenter.lng();
      
      // Update Coords immediately
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
            const addr = results[0].formatted_address;
            setInputValue(addr);
            onChange({ address: addr, lat, lng });
        }
      });
    }
  }, [map, geocoder, onChange]);

  // Handle Autocomplete Text Search
  const onPlaceSelected = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const addr = place.formatted_address || place.name;

        setCenter({ lat, lng });
        setInputValue(addr);
        onChange({ address: addr, lat, lng });
      }
    }
  };

  // Handle Manual Typing
  const handleManualInput = (e) => {
    setInputValue(e.target.value);
    // We pass existing lat/lng so we don't break the map while typing
    onChange({
      address: e.target.value,
      lat: latitude,
      lng: longitude
    });
  };

  if (!isLoaded) return <div className="h-[400px] w-full bg-gray-100 flex items-center justify-center">Loading Map...</div>;

  return (
    <div className="space-y-3">
      {label && (
        <Label className="flex items-center gap-2 text-lg font-semibold">
          <MapPin className="w-5 h-5" /> {label}
        </Label>
      )}

      <div className="relative">
        <Autocomplete
          onLoad={(ref) => setAutocomplete(ref)}
          onPlaceChanged={onPlaceSelected}
          options={{ types: ['geocode', 'establishment'], componentRestrictions: { country: "ca" } }}
        >
          <Input
            type="text"
            disabled={disabled}
            placeholder="Search address"
            value={inputValue} // BOUND TO INTERNAL STATE
            onChange={handleManualInput}
            className={`w-full py-6 bg-gray-100 z-10 ${error ? "border-red-500 ring-red-500" : ""}`}
          />
        </Autocomplete>
      </div>

      <div className="rounded-lg hidden border border-gray-200">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "400px" }}
          center={center}
          zoom={15}
          onLoad={onLoad}
          onDragEnd={onMapDragEnd}
          options={{ streetViewControl: false, mapTypeControl: false }}
        >
          <Marker 
            position={latitude && longitude ? { lat: parseFloat(latitude), lng: parseFloat(longitude) } : center}
            animation={window.google.maps.Animation.DROP}
          />
        </GoogleMap>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );
};